import {
	IonButton,
	IonButtons,
	IonContent,
	IonHeader,
	IonPage,
	IonTitle,
	IonToolbar,
} from "@ionic/react";
import { BrowserMultiFormatOneDReader, IScannerControls } from "@zxing/browser";
import {
	BarcodeFormat,
	DecodeHintType,
	Exception,
	Result,
} from "@zxing/library";
import { isEmpty } from "lodash";
import { useEffect, useRef, useState } from "react";
import "./BarcodeScanner.css";
import { useTranslation } from "react-i18next";

interface BarcodeScannerProps {
	onDismiss(): void;
	onFoundResult(text: string): void;
}

const formats = [BarcodeFormat.CODE_39];
const hints = new Map();
const regex: RegExp = /[a-zA-Z]/g;
hints.set(DecodeHintType.ALLOWED_LENGTHS, [8]);
hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);

function isValidBarcode(code: string): boolean {
	return !isEmpty(code) && code.length === 8 && !code.match(regex);
}

function BarcodeScanner({ onDismiss, onFoundResult }: BarcodeScannerProps) {
	const controlsRef = useRef<boolean>(true);
	const readerRef = useRef(new BrowserMultiFormatOneDReader(hints));
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const [showBarcode, setShowBarcode] = useState(true);

	const { t } = useTranslation();

	useEffect(() => {
		startCapture();
		const timer = setTimeout(() => {
			setShowBarcode(false);
		  }, 3000); // Hide the barcode after 3 seconds
	  
		  return () => {
			clearTimeout(timer);
		  };
	}, []);

	function handleDismiss() {
		controlsRef.current = false;
		onDismiss();
	}

	async function startCapture() {
		if (!videoRef.current) {
			return;
		}

		await readerRef.current.decodeFromConstraints(
			{
				audio: false,
				video: {
					facingMode: "environment",
				},
			},
			videoRef.current,
			function (
				result: Result | undefined,
				_error: Exception | undefined,
				controls: IScannerControls
			) {
				if (controlsRef.current === false) {
					controls.stop();
					return;
				}
				if (typeof result !== "undefined") {
					if (isValidBarcode(result.getText())) {
						controls.stop();
						onFoundResult(result.getText());
					}
				}
			}
		);
	}

	return (
		<IonPage>
			<IonHeader>
				<IonToolbar>
					<IonButtons slot="start">
						<IonButton color="medium" onClick={handleDismiss}>
							{t("general.cancel")}
						</IonButton>
					</IonButtons>
					<IonTitle>{t("dataCollection.scanPageTitle")}</IonTitle>
				</IonToolbar>
			</IonHeader>
			<IonContent fullscreen>
				<div className="top-right" />
				<div className="top-left" />
				<div className="bottom-right" />
				<div className="bottom-left" />
				<video ref={videoRef} style={{ height: "90%", width: "auto", margin: "auto" }} />
				{showBarcode && (
					<img
						src="https://static.vecteezy.com/system/resources/previews/022/722/106/non_2x/barcode-qr-code-transparent-free-free-png.png"
						alt="barcode-scanner"
						className={showBarcode ? 'barcode-img' : 'barcode-img hidden'}
					/>
				)}
			</IonContent>
		</IonPage>
	);
}

export default BarcodeScanner;
